// Updated src/stores/authStore.js - Add socket authentication + device tracking
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { authAPI, twoFactorAPI, userAPI } from '@/services/api'
import socketService from '@/services/socket'
import { generateFingerprint } from '@/utils/fingerprint'
import toast from 'react-hot-toast'

export const useAuthStore = create(
    persist(
        (set, get) => ({
            // State
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
            // 2FA pending state
            pending2FA: null, // { userId } when awaiting 2FA code

            // Actions
            login: async (credentials) => {
                set({ isLoading: true, error: null, pending2FA: null })

                try {
                    // Generate browser fingerprint for device tracking
                    let clientFingerprint = null
                    try { clientFingerprint = await generateFingerprint() } catch {}

                    const response = await authAPI.login({ ...credentials, clientFingerprint })

                    // Check if 2FA is required (either user-enabled or unknown device)
                    if (response.requires2FA) {
                        set({
                            isLoading: false,
                            pending2FA: {
                                userId: response.userId,
                                reason: response.reason,
                                deviceInfo: response.deviceInfo,
                                clientFingerprint,
                            },
                        })
                        const msg = response.reason === 'unknown_device'
                            ? 'Unrecognized device — verification code sent to your email'
                            : 'Verification code sent to your email'
                        toast(msg, { icon: response.reason === 'unknown_device' ? '🔒' : '📧' })
                        return { success: true, requires2FA: true, reason: response.reason }
                    }

                    const { token, user } = response

                    // Store auth data
                    authAPI.setAuth(token, user)

                    // Update state
                    set({
                        user,
                        token,
                        isAuthenticated: true,
                        isLoading: false,
                        error: null,
                        pending2FA: null,
                    })

                    // Connect to socket with authentication
                    await socketService.connect(token)

                    toast.success(`Welcome back, ${user.username}!`)
                    return { success: true }
                } catch (error) {
                    const errorMessage = error.response?.data?.error || 'Login failed'
                    set({
                        isLoading: false,
                        error: errorMessage,
                        isAuthenticated: false
                    })
                    return { success: false, error: errorMessage }
                }
            },

            // Verify 2FA code to complete login
            verify2FA: async (code) => {
                const { pending2FA } = get()
                if (!pending2FA) {
                    return { success: false, error: 'No pending 2FA verification' }
                }

                set({ isLoading: true, error: null })
                try {
                    const response = await twoFactorAPI.verifyLogin(
                        pending2FA.userId, code,
                        pending2FA.clientFingerprint
                    )
                    const { token, user } = response

                    authAPI.setAuth(token, user)

                    set({
                        user,
                        token,
                        isAuthenticated: true,
                        isLoading: false,
                        error: null,
                        pending2FA: null,
                    })

                    await socketService.connect(token)

                    toast.success(`Welcome back, ${user.username}!`)
                    return { success: true }
                } catch (error) {
                    const errorMessage = error.response?.data?.error || 'Invalid verification code'
                    set({ isLoading: false, error: errorMessage })
                    return { success: false, error: errorMessage }
                }
            },

            cancel2FA: () => {
                set({ pending2FA: null, error: null })
            },

            register: async (userData) => {
                set({ isLoading: true, error: null })

                try {
                    const response = await authAPI.register(userData)
                    const { token, user } = response

                    // Store auth data
                    authAPI.setAuth(token, user)

                    // Update state
                    set({
                        user,
                        token,
                        isAuthenticated: true,
                        isLoading: false,
                        error: null
                    })

                    // Connect to socket with authentication
                    await socketService.connect(token)

                    toast.success(`Welcome to BaraquBot, ${user.username}!`)
                    return { success: true }
                } catch (error) {
                    const errorMessage = error.response?.data?.error || 'Registration failed'
                    set({
                        isLoading: false,
                        error: errorMessage,
                        isAuthenticated: false
                    })
                    return { success: false, error: errorMessage }
                }
            },

            logout: () => {
                // Clear auth data
                authAPI.logout()

                // Disconnect socket
                socketService.disconnect()

                // Reset state
                set({
                    user: null,
                    token: null,
                    isAuthenticated: false,
                    isLoading: false,
                    error: null
                })

                toast.success('Logged out successfully')
            },

            // Refresh user profile from server (call after preference changes)
            refreshProfile: async () => {
                try {
                    const freshUser = await userAPI.getProfile()
                    set({ user: freshUser })
                    // Also update localStorage cache
                    authAPI.setCurrentUser(freshUser)
                } catch (error) {
                    console.error('Failed to refresh profile:', error)
                }
            },

            // Initialize auth state from stored data
            initialize: async () => {
                try {
                    const token = authAPI.getToken()
                    const user = authAPI.getCurrentUser()

                    if (token && user) {
                        set({
                            user,
                            token,
                            isAuthenticated: true,
                            isLoading: false
                        })

                        // Fetch fresh profile in background to pick up preference changes
                        userAPI.getProfile().then(freshUser => {
                            set({ user: freshUser })
                            authAPI.setCurrentUser(freshUser)
                        }).catch(() => {})

                        // Connect to socket with authentication
                        await socketService.connect(token)
                    } else {
                        // Clear any invalid stored data
                        authAPI.logout()
                        set({
                            user: null,
                            token: null,
                            isAuthenticated: false,
                            isLoading: false
                        })
                    }
                } catch (error) {
                    console.error('Auth initialization error:', error)
                    // Clear any corrupted data
                    authAPI.logout()
                    set({
                        user: null,
                        token: null,
                        isAuthenticated: false,
                        isLoading: false,
                        error: null
                    })
                }
            },

            // Clear error
            clearError: () => set({ error: null })
        }),
        {
            name: 'auth-storage',
            partialize: (state) => ({
                user: state.user,
                token: state.token,
                isAuthenticated: state.isAuthenticated
            }),
            // Add error handling for storage
            onRehydrateStorage: () => (state) => {
                if (state) {
                    // Initialize after rehydration
                    state.initialize?.()
                }
            }
        }
    )
)